// Client-side log instrumentation — imported by client components
// Captures errors, warnings, fetch failures, and forwards to /api/logs

interface LogEvent {
	level: 'error' | 'warn' | 'info';
	source: string;
	message: string;
	stack?: string;
	data?: Record<string, any>;
	timestamp: string;
	sessionId: string;
}

let sessionId = '';
const buffer: LogEvent[] = [];
let flushTimer: ReturnType<typeof setInterval> | null = null;
let initialized = false;
// Set when the page is navigating away. In-flight fetches get aborted by the
// browser during unload — those failures are not real errors, so we suppress them.
let isUnloading = false;
const seen = new Map<string, number>();
const MAX_BUFFER = 50;
const FLUSH_INTERVAL = 5_000;
const DEDUP_WINDOW = 5_000;
function getIngestKey(): string {
	return process.env.NEXT_PUBLIC_LOG_INGEST_KEY ?? '';
}

function fingerprint(event: LogEvent): string {
	return `${event.message}::${event.stack?.slice(0, 200) ?? ''}`;
}

function enqueue(event: LogEvent) {
	const fp = fingerprint(event);
	const now = Date.now();
	const lastSeen = seen.get(fp);
	if (lastSeen && now - lastSeen < DEDUP_WINDOW) return;
	seen.set(fp, now);

	if (buffer.length >= MAX_BUFFER) {
		buffer.shift();
	}
	buffer.push(event);
}

function makeEvent(
	level: 'error' | 'warn' | 'info',
	source: string,
	message: string,
	extra?: { stack?: string; data?: Record<string, any> }
): LogEvent {
	return {
		level,
		source,
		message,
		stack: extra?.stack,
		data: extra?.data,
		timestamp: new Date().toISOString(),
		sessionId,
	};
}

function flush() {
	if (buffer.length === 0) return;
	const events = buffer.splice(0);
	const key = getIngestKey();

	try {
		const body = JSON.stringify(events);
		const url = key ? `/api/logs?key=${encodeURIComponent(key)}` : '/api/logs';
		const headers: Record<string, string> = { 'Content-Type': 'application/json' };
		if (key) headers['x-ingest-key'] = key;

		if (typeof fetch !== 'undefined') {
			fetch(url, {
				method: 'POST',
				headers,
				body,
				keepalive: true,
			}).catch(() => {
				// Silently fail — we don't want logging to cause errors
			});
		}
	} catch {
		// Silently fail — we don't want logging to cause errors
	}

	const now = Date.now();
	for (const [key, ts] of seen) {
		if (now - ts > DEDUP_WINDOW * 2) seen.delete(key);
	}
}

function flushBeacon() {
	if (buffer.length === 0) return;
	const events = buffer.splice(0);
	const key = getIngestKey();

	try {
		const body = JSON.stringify(events);
		const url = key ? `/api/logs?key=${encodeURIComponent(key)}` : '/api/logs';
		if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
			navigator.sendBeacon(url, new Blob([body], { type: 'application/json' }));
		}
	} catch {
		// Silently fail — we don't want logging to cause errors
	}
}

export function logError(message: string, extra?: { stack?: string; data?: Record<string, any> }) {
	if (!initialized) return;
	enqueue(makeEvent('error', 'client', message, extra));
}

export function logWarn(message: string, extra?: { data?: Record<string, any> }) {
	if (!initialized) return;
	enqueue(makeEvent('warn', 'client', message, extra));
}

export function logInfo(message: string, extra?: { data?: Record<string, any> }) {
	if (!initialized) return;
	enqueue(makeEvent('info', 'client', message, extra));
}

export function initClientLogging() {
	if (initialized) return;
	if (typeof window === 'undefined') return;
	initialized = true;
	sessionId = crypto.randomUUID();

	const prevOnError = window.onerror;
	window.onerror = function (message, source, lineno, colno, error) {
		enqueue(makeEvent('error', 'client', String(message), {
			stack: error?.stack,
			data: { source, lineno, colno },
		}));
		if (prevOnError) return prevOnError.call(this, message, source, lineno, colno, error);
		return false;
	};

	const prevOnUnhandled = window.onunhandledrejection;
	window.onunhandledrejection = function (event: PromiseRejectionEvent) {
		const reason = event.reason;
		const message = reason instanceof Error ? reason.message : String(reason);
		const stack = reason instanceof Error ? reason.stack : undefined;
		enqueue(makeEvent('error', 'client:promise', message, { stack }));
		if (prevOnUnhandled) return (prevOnUnhandled as any).call(this, event);
	};

	// console.error/warn are intentionally not monkey-patched — DevTools would then show
	// errors as originating from log-client.ts instead of the actual call site.
	const origFetch = globalThis.fetch;
	globalThis.fetch = async function (input: RequestInfo | URL, init?: RequestInit) {
		const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;

		// Skip logging for our own log endpoint
		if (url.includes('/api/logs')) {
			return origFetch.call(this, input, init);
		}

		// On same-origin requests, propagate this browser session's ID plus a fresh
		// per-request ID so the server can correlate its logs back to this client
		// session/request (see src/lib/requestContext.ts). Skipped cross-origin to
		// avoid leaking the IDs to third parties (and triggering CORS preflights).
		let requestId: string | undefined;
		let callInit = init;
		let sameOrigin = false;
		try {
			sameOrigin = new URL(url, window.location.href).origin === window.location.origin;
		} catch {
			sameOrigin = false;
		}
		if (sameOrigin) {
			requestId = crypto.randomUUID();
			const isRequestObj = typeof input !== 'string' && !(input instanceof URL);
			// Mirror fetch's precedence: init.headers wins, else the Request's own headers.
			const baseHeaders = init?.headers ?? (isRequestObj ? (input).headers : undefined);
			const headers = new Headers(baseHeaders);
			headers.set('x-client-session-id', sessionId);
			headers.set('x-client-request-id', requestId);
			callInit = { ...init, headers };
		}

		const start = Date.now();
		try {
			const response = await origFetch.call(this, input, callInit);
			const duration = Date.now() - start;
			if (!response.ok) {
				enqueue(makeEvent('warn', 'client:fetch', 'fetch response not ok', {
					data: { url, method: init?.method ?? 'GET', status: response.status, duration, requestId },
				}));
			}
			return response;
		} catch (error) {
			const duration = Date.now() - start;
			const errorMessage = error instanceof Error ? error.message : String(error);

			// Intentional cancellations (AbortController: superseded requests, component
			// unmount, SPA navigation) are not errors — the app aborts them on purpose.
			const isAbort =
				(error as any)?.name === 'AbortError' ||
				init?.signal?.aborted === true ||
				/\baborted\b/i.test(errorMessage);

			// Failures while the page is unloading are the browser killing in-flight
			// requests, not real network errors.
			if (!isAbort && !isUnloading) {
				enqueue(makeEvent('error', 'client:fetch', 'fetch network error', {
					stack: error instanceof Error ? error.stack : undefined,
					data: { url, method: init?.method ?? 'GET', error: errorMessage, duration, requestId },
				}));
			}
			throw error;
		}
	};

	if (typeof window.ReportingObserver !== 'undefined') {
		try {
			const observer = new window.ReportingObserver((reports) => {
				for (const report of reports) {
					enqueue(makeEvent('warn', 'client:report', `${report.type}: ${(report.body as any)?.message ?? JSON.stringify(report.body)}`, {
						data: { type: report.type, body: report.body },
					}));
				}
			}, { types: ['deprecation', 'intervention'], buffered: true });
			observer.observe();
		} catch {
			// ReportingObserver not fully supported
		}
	}

	flushTimer = setInterval(flush, FLUSH_INTERVAL);

	document.addEventListener('visibilitychange', () => {
		if (document.visibilityState === 'hidden') flushBeacon();
	});
	window.addEventListener('pagehide', () => {
		isUnloading = true;
		flushBeacon();
	});
	window.addEventListener('beforeunload', () => {
		isUnloading = true;
	});
}
